import React, { createContext, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getReactToken, JWTResponseDTO, logoutSession, validateToken } from '../api/auth.api';
import { setAuthToken } from '../providers/authTokenProvider';
import { getEnvironmentVariable } from '../utils';

type AuthContextType = {
    tokens: {
        sessionToken: string;
        reactToken: string;
    };
    setSessionToken: (token: string) => void;
    setUserInfo: React.Dispatch<SetStateAction<JWTResponseDTO['user'] | null>>;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
    userInfo: JWTResponseDTO['user'] | null;
    setSessionTokenLoading: React.Dispatch<React.SetStateAction<boolean>>;
    logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const platformURL = getEnvironmentVariable('REACT_APP_PLATFORM_URL');
const LOGIN_URL = `${platformURL}/login`;

export const AuthProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const [ tokens, setTokens ] = useState<{
        sessionToken: string;
        reactToken: string;
    }>({
        reactToken: '',
        sessionToken: '',
    });
    const [ sessionTokenLoading, setSessionTokenLoading ] = useState(true);
    const [ reactTokenLoading, setReactTokenLoading ] = useState(true);
    const [ userInfo, setUserInfo ] = useState<JWTResponseDTO['user'] | null>(null);
    const isInitiated = useRef(false);
    const isRedirected = useRef(false);

    useEffect(() => {
        if (isInitiated.current) {
            return;
        }

        isInitiated.current = true;
        const reactToken = localStorage.getItem('reactToken') || '';
        const sessionToken = localStorage.getItem('sessionToken') || '';
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        if (sessionToken && user) {
            validateToken(sessionToken)
            .then((isTokenValid) => {
                if (isTokenValid) {
                    setTokens(tokens => (
                        { ...tokens, sessionToken }
                    ));
                    setAuthToken('sessionToken', sessionToken);
                    // @ts-ignore
                    setUserInfo(user);
                } else {
                    resetSessionInfo();
                }

            })
            .catch((e) => {
                if (e.status === 401) {
                    resetSessionInfo();
                }
            })
            .finally(() => setSessionTokenLoading(false));
        } else {
            resetSessionInfo();
        }

        if (reactToken) {
            validateToken(reactToken)
            .then((isTokenValid) => {
                if (isTokenValid) {
                    setTokens(tokens => (
                        { ...tokens, reactToken }
                    ));
                    setAuthToken('reactToken', reactToken);
                } else {
                    resetReactInfo();
                }
            })
            .catch((e) => {
                if (e.status === 401) {
                    resetReactInfo();
                }
            })
            .finally(() => setReactTokenLoading(false));
        } else {
            getReactToken()
            .then((response) => {
                const { token } = response;
                setTokens(tokens => (
                    { ...tokens, reactToken }
                ));
                localStorage.setItem('reactToken', token);
                setAuthToken('reactToken', token);
            })
            .finally(() => {
                setReactTokenLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setSessionToken = useCallback((sessionToken: string) => {
        setTokens(tokens => (
            { ...tokens, sessionToken }
        ));
        setAuthToken('sessionToken', sessionToken);
    }, []);

    const resetReactInfo = () => {
        localStorage.removeItem('reactToken');
        setTokens(tokens => (
            {
                reactToken: '',
                sessionToken: tokens.sessionToken,
            }
        ));
    };

    const resetSessionInfo = () => {
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('user');
        setTokens(tokens => (
            {
                reactToken: tokens.reactToken,
                sessionToken: '',
            }
        ));
        setUserInfo(null);
    };

    const onLogout = useCallback(() => {
        logoutSession().finally(() => {
            resetSessionInfo();
            window.location.href = LOGIN_URL;
        });
    }, []);

    useEffect(() => {
        if (isRedirected.current) {
            return;
        }

        if (sessionTokenLoading || reactTokenLoading) {
            return;
        }

        if (!tokens.sessionToken || !tokens.reactToken) {
            const { search, pathname } = window.location;
            let urlToSave = '';

            if (pathname !== '/') {
                urlToSave = pathname;
            }

            if (search) {
                urlToSave += search;
            }

            if (urlToSave) {
                localStorage.setItem('preLoginURL', urlToSave);
            }
            window.location.href = LOGIN_URL;
            return;
        }

        isRedirected.current = true;
    }, [ sessionTokenLoading, reactTokenLoading, tokens.sessionToken, tokens.reactToken ]);

    return (
        <AuthContext.Provider
            value={{
                tokens,
                setSessionToken,
                isAuthenticated: !!tokens.sessionToken && !!tokens.reactToken,
                isAuthLoading: sessionTokenLoading || reactTokenLoading,
                userInfo,
                setUserInfo,
                setSessionTokenLoading,
                logout: onLogout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
};